import { parse, stringify } from 'query-string';
import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { Validator } from '../forms/validators';
export type QueryParamParser<TValue> = (newValue: string) => TValue;
export type QueryParamFormatter<TValue> = (value: NonNullable<TValue>) => string;
export type QueryParamConfig<TValue> = (
  | {
      parser: QueryParamParser<TValue>;
    }
  | {
      /**
       * A callback that parses the string query-parameter value into the data-type
       * you want for your parameter.
       *
       * Return undefined if the value is un-parsable
       *
       * @see `@sd/shared.parsers` for pre-defined parsers
       */
      parser: QueryParamParser<TValue>;
      /**
       * The default value assigned to your query parameter when the parameter
       * is omitted from the query string
       */
      defaultValue: NonNullable<TValue>;
    }
  | {
      /**
       * A callback that parses the string query-parameter value into the data-type
       * you want for your parameter.
       *
       * Return undefined if the value is un-parsable
       *
       * @see `@sd/shared.parsers` for pre-defined parsers
       */
      parser: QueryParamParser<TValue | undefined>;
    }
) & {
  /**
   * A callback that formats a value into your desired stringified query parameter.
   *
   * Use this when you have complex types like Arrays, Dates, or other complex objects
   * that may have specific formats when used in a query parameter
   *
   * NOTE: The formatter must be compatible with your parser, ie:
   * formatter(parser(<STRINGIFIED-THING>)) === <STRINGIFIED-THING>
   */
  formatter?: QueryParamFormatter<TValue>;

  /**
   * Validates your parsed value, eg is within a range, or is an integer, etc
   *
   * If a query-string value does NOT pass validation, it is omitted and the
   * default value (if any) is used instead.
   *
   * @see `@sd/shared.validators` for pre-defined validators
   */
  validators?: Validator | Validator[];

  /**
   * Allows skipping auto-reformatting/cleanup on initialization
   */
  skipReformatOnInit?: true;
};

export type QueryParamSetter<TValue> = (newValue: TValue, options?: { replace?: boolean }) => void;
export type QueryParamSearchGenerator<TValue> = (newValue: TValue, fromQuery: string) => string;

export type UnboxedConfig<TConfig extends QueryParamConfig<any>> = TConfig extends { defaultValue: any }
  ? TConfig['defaultValue']
  : ReturnType<TConfig['parser']>;

/**
 * Creates a state variable that tracks a query-string parameter
 */
export function useQueryParam<TConfig extends QueryParamConfig<any>>(
  key: string,
  config: TConfig
): Readonly<
  [UnboxedConfig<TConfig>, QueryParamSetter<UnboxedConfig<TConfig>>, QueryParamSearchGenerator<UnboxedConfig<TConfig>>]
>;
/**
 * Creates a state variable that tracks a query-string parameter
 */
export function useQueryParam<TValue>(
  key: string,
  config: QueryParamConfig<TValue>
): Readonly<[TValue, QueryParamSetter<TValue>, QueryParamSearchGenerator<TValue>]> {
  const history = useHistory();
  const defaultValue = 'defaultValue' in config ? config.defaultValue : undefined;

  const formatter = (value: TValue | undefined) => {
    if (value === undefined) return undefined;
    if (config.formatter) return config.formatter(value!);
    return String(value);
  };
  const formattedDefault = useMemo(
    () => (defaultValue === undefined ? undefined : formatter(defaultValue)),
    [defaultValue]
  );

  const computeParam = (): TValue => {
    const parsedQueryString = parse(history.location.search);
    const rawParam = parsedQueryString[key] ? String(parsedQueryString[key]) : undefined;
    const parsedValue = rawParam ? config.parser(rawParam) || defaultValue : defaultValue;
    const validators = config.validators
      ? Array.isArray(config.validators)
        ? config.validators
        : [config.validators]
      : [];
    const hasErrors = validators.some(validator => validator(parsedValue, key));
    return (hasErrors ? defaultValue : parsedValue) as TValue;
  };
  const [param, setParam] = useState(computeParam);

  useEffect(() => {
    const handler = () => {
      const newValue = computeParam();
      setParam(oldValue => (formatter(oldValue) === formatter(newValue) ? oldValue : newValue));
    };
    const unlisten = history.listen(handler);
    return unlisten;
  }, []);

  /**
   * Generates the next state of a query parameter without applying the changes to the URL
   */
  const generate = useMemo<QueryParamSearchGenerator<TValue>>(
    () => (newValue, fromSearch) => {
      const formattedParam = formatter(newValue!);
      return (
        stringify(
          {
            ...parse(fromSearch),
            [key]: formattedParam === formattedDefault ? undefined : formattedParam
          },
          { skipEmptyString: true, skipNull: true }
        )
          // always replace escaped commas with real commas for more readable URLs
          .replace(/%2C/g, ',')
      );
    },
    []
  );

  /**
   * Sets or replaces the parameters in the URL
   */
  const setter = useMemo<QueryParamSetter<TValue>>(
    () => (newValue, setConfig) => {
      const newSearch = generate(newValue, history.location.search);
      if (newSearch !== history.location.search.substr(1)) {
        (setConfig?.replace ? history.replace : history.push)({ search: newSearch && `?${newSearch}` });
      }
    },
    []
  );

  useEffect(() => {
    if (!config.skipReformatOnInit) setter(param, { replace: true });
  }, []);

  return [param, setter, generate] as const;
}
